/**
 * remediationActionMeta.js
 *
 * Single source of truth describing every remediation wizard action type, mirrored
 * from the backend RemediationActionType constants and the engine's per-action config
 * DTOs (EndpointRemediationService.cs). Each entry declares:
 *   - value / label / description for the action picker
 *   - icon name (react-icons/fa) for visual consistency
 *   - capability: the ExternalActionGuard capability that gates it (null = no side effect)
 *   - fields: the editable config fields rendered by RemediationStepEditor, each shaped
 *             { key, label, type, placeholder?, options?, help?, rows? }
 *
 * Keeping this metadata centralised makes the editor fully extensible: adding a new
 * action type is a matter of adding one entry here plus the backend handler.
 *
 * @module SettingsPages/components/remediation/remediationActionMeta
 */

/** Action type keys — must match backend RemediationActionType. */
export const ACTION_TYPES = {
    RunScript: 'RunScript',
    DeviceAction: 'DeviceAction',
    InstallApp: 'InstallApp',
    UninstallApp: 'UninstallApp',
    SendNotification: 'SendNotification',
    GraphApiCall: 'GraphApiCall',
    WebhookCall: 'WebhookCall',
    PurviewAction: 'PurviewAction',
    WaitForCondition: 'WaitForCondition',
    ApprovalGate: 'ApprovalGate',
    LogMessage: 'LogMessage'
};

/** Remediation categories — must match backend RemediationCategory.All. */
export const REMEDIATION_CATEGORIES = [
    'Storage', 'Security', 'Compliance', 'Performance', 'Software',
    'Certificates', 'Connectivity', 'Battery', 'Updates', 'Access'
];

/** Risk levels shared with wizard/script forms. */
export const RISK_LEVELS = ['Low', 'Medium', 'High'];

/** Script platforms supported by the library + RunScript steps. */
export const SCRIPT_PLATFORMS = ['PowerShell', 'Bash'];

/**
 * Notification channel keys for SendNotification steps. Mirrors the backend
 * NotificationChannelTypes constants (Email / Teams / WhatsApp) so the wizard
 * offers the same delivery channels the engine knows how to dispatch.
 */
export const NOTIFICATION_CHANNELS = ['Email', 'Teams', 'WhatsApp'];

/**
 * Recipient types for SendNotification steps. These describe *who* the notification
 * targets and mirror the documented config schema on EndpointRemediationWizard /
 * WorkflowAutomationStep (DeviceOwner, ITGroup, Role, …).
 */
export const NOTIFICATION_RECIPIENT_TYPES = ['DeviceOwner', 'ITGroup', 'Role', 'AssignedTechnician'];

/**
 * Ordered list of action types with their UI metadata and editable config fields.
 * The `fields` here map 1:1 to the JSON keys each backend handler deserialises.
 */
export const ACTION_META = [
    {
        value: ACTION_TYPES.RunScript,
        label: 'Run Script',
        icon: 'FaTerminal',
        capability: 'IntuneRunScript',
        description: 'Dispatch a PowerShell / Bash script to the device via Intune.',
        fields: [
            { key: 'platform', label: 'Platform', type: 'select', options: SCRIPT_PLATFORMS, help: 'Shell the script targets.' },
            { key: 'script', label: 'Script Body', type: 'code', rows: 10, placeholder: '# PowerShell / Bash script to run on the device', help: 'The exact script body dispatched to the endpoint. You can load one from the Script Library.' }
        ]
    },
    {
        value: ACTION_TYPES.DeviceAction,
        label: 'Device Action',
        icon: 'FaDesktop',
        capability: 'IntuneDeviceAction',
        description: 'Trigger a built-in Intune device action (sync or reboot).',
        fields: [
            { key: 'action', label: 'Action', type: 'select', options: ['syncDevice', 'reboot'], help: 'The Intune managed-device action to trigger.' }
        ]
    },
    {
        value: ACTION_TYPES.InstallApp,
        label: 'Install App',
        icon: 'FaDownload',
        capability: 'GraphApiWrite',
        description: 'Install a managed application on the device.',
        fields: [
            { key: 'appId', label: 'App ID', type: 'text', placeholder: 'Intune managed app ID', help: 'The managed application GUID to install.' },
            { key: 'appName', label: 'App Name (display)', type: 'text', placeholder: 'e.g. Company Portal', help: 'Friendly name shown in the preview.' }
        ]
    },
    {
        value: ACTION_TYPES.UninstallApp,
        label: 'Uninstall App',
        icon: 'FaTrashAlt',
        capability: 'GraphApiWrite',
        description: 'Uninstall an application from the device.',
        fields: [
            { key: 'appId', label: 'App ID', type: 'text', placeholder: 'Intune managed app ID', help: 'The managed application GUID to remove.' },
            { key: 'appName', label: 'App Name (display)', type: 'text', placeholder: 'e.g. Legacy Agent', help: 'Friendly name shown in the preview.' }
        ]
    },
    {
        value: ACTION_TYPES.SendNotification,
        label: 'Send Notification',
        icon: 'FaBell',
        capability: 'OutboundNotification',
        description: 'Queue a notification (Teams / Email / WhatsApp via channels).',
        fields: [
            { key: 'channel', label: 'Channel', type: 'select', options: NOTIFICATION_CHANNELS, allowCustom: true, help: 'Notification channel key. Pick a configured channel or choose “Other…” for a custom key.' },
            { key: 'recipientType', label: 'Recipient Type', type: 'select', options: NOTIFICATION_RECIPIENT_TYPES, allowCustom: true, help: 'Who receives the notification. Choose “Other…” to enter a custom recipient type.' },
            { key: 'message', label: 'Message', type: 'textarea', rows: 3, placeholder: 'Notification body', help: 'Supports {deviceId} substitution downstream.' }
        ]
    },
    {
        value: ACTION_TYPES.GraphApiCall,
        label: 'Graph API Call',
        icon: 'FaProjectDiagram',
        capability: 'GraphApiWrite',
        description: 'Call a Microsoft Graph endpoint (advanced).',
        fields: [
            { key: 'method', label: 'HTTP Method', type: 'select', options: ['GET', 'POST', 'PATCH', 'PUT', 'DELETE'] },
            { key: 'endpoint', label: 'Endpoint', type: 'text', placeholder: '/deviceManagement/managedDevices/{deviceId}', help: 'Relative Graph path. {deviceId} is substituted at runtime.' },
            { key: 'body', label: 'Body (JSON)', type: 'code', rows: 5, placeholder: '{ }', help: 'Request body for write methods.' }
        ]
    },
    {
        value: ACTION_TYPES.WebhookCall,
        label: 'Webhook Call',
        icon: 'FaLink',
        capability: 'WebhookOutbound',
        description: 'Send an outbound HTTP request to an external system.',
        fields: [
            { key: 'url', label: 'Webhook URL', type: 'text', placeholder: 'https://example.com/hook', help: 'Absolute http(s) URL to call.' },
            { key: 'method', label: 'HTTP Method', type: 'select', options: ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'] },
            { key: 'authorizationHeader', label: 'Authorization Header', type: 'text', placeholder: 'Bearer …', help: 'Optional Authorization header value.' },
            { key: 'timeoutSeconds', label: 'Timeout (seconds)', type: 'number', placeholder: '30' },
            { key: 'body', label: 'Body (JSON)', type: 'code', rows: 4, placeholder: '{ "deviceId": "{deviceId}" }', help: '{deviceId} is substituted at runtime.' }
        ]
    },
    {
        value: ACTION_TYPES.PurviewAction,
        label: 'Purview Action',
        icon: 'FaUserShield',
        capability: 'PurviewWrite',
        description: 'Perform a Microsoft Purview compliance write action.',
        fields: [
            { key: 'action', label: 'Purview Action', type: 'select', options: ['resolveDlpAlert', 'applyRetentionLabel'], help: 'Which Purview mutation to perform.' },
            { key: 'alertId', label: 'DLP Alert ID', type: 'text', placeholder: 'alert id', help: 'Required for resolveDlpAlert.' },
            { key: 'status', label: 'New Status', type: 'select', options: ['new', 'inProgress', 'resolved'], help: 'Target DLP alert status (Microsoft Graph values).' },
            { key: 'classification', label: 'Classification', type: 'text', placeholder: 'e.g. truePositive', help: 'Optional alert classification.' },
            { key: 'comment', label: 'Comment', type: 'textarea', rows: 2, placeholder: 'Audit comment' },
            { key: 'driveId', label: 'Drive ID', type: 'text', placeholder: 'drive id', help: 'Required for applyRetentionLabel.' },
            { key: 'itemId', label: 'Item ID', type: 'text', placeholder: 'drive item id', help: 'Required for applyRetentionLabel.' },
            { key: 'retentionLabelName', label: 'Retention Label', type: 'text', placeholder: 'e.g. Legal Hold', help: 'Label to apply for applyRetentionLabel.' }
        ]
    },
    {
        value: ACTION_TYPES.WaitForCondition,
        label: 'Wait',
        icon: 'FaClock',
        capability: null,
        description: 'Pause for a fixed delay before continuing (no device impact).',
        fields: [
            { key: 'delaySeconds', label: 'Delay (seconds)', type: 'number', placeholder: '5', help: 'How long to wait before the next step (0–600).' }
        ]
    },
    {
        value: ACTION_TYPES.ApprovalGate,
        label: 'Approval Gate',
        icon: 'FaUserCheck',
        capability: null,
        description: 'Pause the wizard until a human approves continuation.',
        fields: [
            { key: 'approverHint', label: 'Approver Hint', type: 'text', placeholder: 'e.g. IT Manager', help: 'Who should approve before continuing.' }
        ]
    },
    {
        value: ACTION_TYPES.LogMessage,
        label: 'Log Message',
        icon: 'FaRegStickyNote',
        capability: null,
        description: 'Write an audit log entry (no device impact).',
        fields: [
            { key: 'message', label: 'Message', type: 'textarea', rows: 2, placeholder: 'Audit note', help: 'Recorded in the execution audit trail.' },
            { key: 'severity', label: 'Severity', type: 'select', options: ['Information', 'Warning', 'Error'] }
        ]
    }
];

/** Lookup map keyed by action value for O(1) access. */
export const ACTION_META_MAP = ACTION_META.reduce((acc, m) => {
    acc[m.value] = m;
    return acc;
}, {});

/**
 * Parse a step's ActionConfigJson into an object, tolerating null / malformed input.
 * @param {string|null|undefined} json
 * @returns {object}
 */
export function parseConfig(json) {
    if (!json) return {};
    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

/**
 * Parse a step's ExecutionConditionJson into { source, operator, value }.
 * @param {string|null|undefined} json
 */
export function parseCondition(json) {
    const cfg = parseConfig(json);
    return {
        source: cfg.source ?? '',
        operator: cfg.operator ?? 'Always',
        value: cfg.value ?? 'Success'
    };
}

/**
 * Serialise a condition object back to JSON, returning null when the step always runs
 * (so we don't persist a redundant gate).
 */
export function serializeCondition(condition) {
    if (!condition || !condition.operator || condition.operator === 'Always') return null;
    if (condition.operator === 'Never') return JSON.stringify({ operator: 'Never' });
    return JSON.stringify({
        source: condition.source || 'PreviousStep',
        operator: condition.operator,
        value: condition.value || 'Success'
    });
}

/** A blank step object used when adding a new step to a wizard. */
export function emptyStep(sortOrder = 0) {
    return {
        endpointRemediationStepId: 0,
        name: '',
        description: '',
        sortOrder,
        actionType: ACTION_TYPES.RunScript,
        actionConfigJson: null,
        continueOnFailure: false,
        timeoutSeconds: 300,
        retryCount: 0,
        retryDelaySeconds: 30,
        executionConditionJson: null,
        icon: '',
        isActive: true
    };
}
